import { useState, useEffect } from 'react';
import { getPuzzleHistory } from '../services/api';
import './HistoryModal.css';

const TYPE_EMOJI = {
  word: '🔤', geo: '🌍', math: '🔢', trivia: '🧠', logic: '🧩'
};

function formatTime(seconds) {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
}

export default function HistoryModal({ onClose }) {
  const [history, setHistory]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');
  const [copied, setCopied]     = useState(null);

  useEffect(() => {
    getPuzzleHistory()
      .then(r => setHistory(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = history.filter(a => {
    if (filter === 'solved') return a.solved;
    if (filter === 'failed') return !a.solved;
    return true;
  });

  const handleShare = async (attempt) => {
    const url = `${window.location.origin}/play/${attempt.puzzle_id}`;
    const text = `🎮 Can you beat my score on PuzzleForge?\n${TYPE_EMOJI[attempt.puzzle_type]} ${attempt.puzzle_type.charAt(0).toUpperCase() + attempt.puzzle_type.slice(1)}Forge — ${formatDate(attempt.puzzle_date)}\n${attempt.solved ? `✅ Solved in ${attempt.num_guesses} tries` : '❌ Not solved'} ${attempt.time_taken_seconds ? `(${formatTime(attempt.time_taken_seconds)})` : ''}\nTry it: ${url}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(attempt.attempt_id);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      prompt('Copy this link:', url);
    }
  };

  const stats = {
    total: history.length,
    solved: history.filter(a => a.solved).length,
    avgTime: Math.round(history.filter(a => a.time_taken_seconds).reduce((s, a) => s + a.time_taken_seconds, 0) / (history.filter(a => a.time_taken_seconds).length || 1)),
  };

  return (
    <div className="hm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="hm-modal">
        <div className="hm-header">
          <div>
            <span className="hm-tag mono">Your Journey</span>
            <h2 className="hm-title mono">Puzzle History</h2>
          </div>
          <button className="hm-close mono" onClick={onClose}>✕</button>
        </div>

        {/* Stats */}
        <div className="hm-stats">
          <div className="hm-stat">
            <span className="hm-stat-value mono">{stats.total}</span>
            <span className="hm-stat-label mono">Played</span>
          </div>
          <div className="hm-stat">
            <span className="hm-stat-value mono">{stats.solved}</span>
            <span className="hm-stat-label mono">Solved</span>
          </div>
          <div className="hm-stat">
            <span className="hm-stat-value mono">{stats.total ? Math.round(stats.solved / stats.total * 100) : 0}%</span>
            <span className="hm-stat-label mono">Win rate</span>
          </div>
          <div className="hm-stat">
            <span className="hm-stat-value mono">{formatTime(stats.avgTime)}</span>
            <span className="hm-stat-label mono">Avg time</span>
          </div>
        </div>

        {/* Filter */}
        <div className="hm-filters">
          {['all', 'solved', 'failed'].map(f => (
            <button
              key={f}
              className={`hm-filter mono ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="hm-list">
          {loading ? (
            <div className="hm-empty mono">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="hm-empty mono">No puzzles yet. Start playing!</div>
          ) : filtered.map(attempt => (
            <div key={attempt.attempt_id} className={`hm-item ${attempt.solved ? 'solved' : 'failed'}`}>
              <div className="hm-item-left">
                <span className="hm-emoji">{TYPE_EMOJI[attempt.puzzle_type] || '🎮'}</span>
                <div>
                  <div className="hm-item-title mono">
                    {attempt.puzzle_type.charAt(0).toUpperCase() + attempt.puzzle_type.slice(1)}Forge
                    <span className={`hm-badge mono ${attempt.solved ? 'solved' : 'failed'}`}>
                      {attempt.solved ? '✓' : '✗'}
                    </span>
                  </div>
                  <div className="hm-item-meta mono">
                    {formatDate(attempt.puzzle_date)} · {attempt.num_guesses} tries · {formatTime(attempt.time_taken_seconds)}
                  </div>
                </div>
              </div>
              <button
                className={`hm-share mono ${copied === attempt.attempt_id ? 'copied' : ''}`}
                onClick={() => handleShare(attempt)}
              >
                {copied === attempt.attempt_id ? '✓ Copied!' : 'Share'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
